import { HelpHintWindow } from "@/components/ui/HintWindow";
import { Panel, PanelGrid } from "@/components/ui/Panel";
import { useLanguage } from "@/context/LanguageContext";
import {
  Squares2X2Icon,
  CalendarDaysIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import { DestructiveTrashIcon } from "@/components/ui/DestructiveTrashIcon";

import { PortfolioPageInsightItem } from "@/components/portfolio/portfolio-page/PortfolioPageInsightItem";
import { PortfolioPageSectionBadge } from "@/components/portfolio/portfolio-page/PortfolioPageSectionBadge";

type WatchDogResultsHintWindowProps = {
  isOpen: boolean;
  onToggle: () => void;
};

export function WatchDogResultsHintWindow({
  isOpen,
  onToggle,
}: WatchDogResultsHintWindowProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      help: "Nápověda",
      title: "Výsledky hlídacího psa",
      description: "Detailní přehled výsledků.",
      hideHelp: "Skrýt nápovědu",
      viewsTitle: "3 přehledy",
      viewsText:
        "Přepínejte mezi 3 přehledy. Kompaktní, detailní a analytický. Každý má něco do sebe.",
      sortingTitle: "Seřazení",
      sortingText: "Nabídky můžete seřadit dle ceny nebo data přidání.",
      favoritesTitle: "Uložit do oblíbených",
      favoritesText:
        "Zajímavou nabídku si můžete uložit do oblíbených. Jednoduše ji pak naleznete mezi oblíbenými a můžete se k ní kdykoliv vrátit.",
      unsuitableTitle: "Nevyhovující nabídka",
      unsuitableText:
        "Nevyhovující nabídku můžete smazat kliknutím na ikonu koše.",
    },
    en: {
      help: "Help",
      title: "Watchdog results",
      description: "Detailed results overview.",
      hideHelp: "Hide help",
      viewsTitle: "3 views",
      viewsText:
        "Switch between 3 views: compact, detailed, and analytics. Each has its strengths.",
      sortingTitle: "Sorting",
      sortingText: "You can sort offers by price or date added.",
      favoritesTitle: "Save to favorites",
      favoritesText:
        "Save an interesting offer to favorites so you can find it easily and return to it anytime.",
      unsuitableTitle: "Unsuitable offer",
      unsuitableText: "Delete an unsuitable offer by clicking the trash icon.",
    },
  }[language];

  if (!isOpen) return null;

  return (
    <HelpHintWindow
      eyebrow={copy.help}
      title={copy.title}
      description={copy.description}
      closeLabel={copy.hideHelp}
      onClose={onToggle}
    >
      <Panel>
        <div className="space-y-2">
          <PortfolioPageSectionBadge type={"dark"}>
            {copy.title}
          </PortfolioPageSectionBadge>
        </div>

        <PanelGrid className="md:grid-cols-2 xl:grid-cols-4">
          <PortfolioPageInsightItem
            icon={Squares2X2Icon}
            title={copy.viewsTitle}
            text={copy.viewsText}
          />
          <PortfolioPageInsightItem
            icon={CalendarDaysIcon}
            title={copy.sortingTitle}
            text={copy.sortingText}
          />
          <PortfolioPageInsightItem
            icon={StarIcon}
            title={copy.favoritesTitle}
            text={copy.favoritesText}
          />
          <PortfolioPageInsightItem
            icon={DestructiveTrashIcon}
            title={copy.unsuitableTitle}
            text={copy.unsuitableText}
          />
        </PanelGrid>
      </Panel>
    </HelpHintWindow>
  );
}
